import { type ProjectCode } from '@constants'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { AnalyticsPlatforms } from '../core/createAnalyticsProviders'
import { type StmSectionNameType } from '../types/fastpass-ga4-events'
import { useAnalytics } from './useAnalytics'

interface UseStmAnalyticsEventProps {
  userId: string
}

export const useStmAnalyticsEvent = (props: UseStmAnalyticsEventProps) => {
  const { userId } = props
  const { sendEvent } = useAnalytics({ defaultPlatforms: [AnalyticsPlatforms.GA4] })

  const sendCrossBrandBannerClickEvent = useCallbackEvent((params: {
    bannerId: number
    bannerPosition: number
    destinationUrl: string
  }) => {
    sendEvent({
      eventName: 'cross_brand_banner_click',
      eventParams: {
        event_category: 'cross_brand_banner_click',
        banner_id: params.bannerId,
        banner_position: params.bannerPosition,
        destination_url: params.destinationUrl,
      },
    })
  })

  const sendBrandNavClickEvent = useCallbackEvent((projectCode: ProjectCode) => {
    if (!userId) return
    sendEvent({
      eventName: 'brand_nav_click',
      eventParams: {
        event_category: 'brand_nav_click',
        project_code: projectCode,
        user_id: userId,
      },
    })
  })

  const sendCrossBrandPageViewEvent = useCallbackEvent((projectCode: ProjectCode) => {
    if (!userId) return
    sendEvent({
      eventName: 'stm_page_view',
      eventParams: {
        event_category: 'page_view',
        project_code: projectCode,
        user_id: userId,
      },
    })
  })

  const sendCrossBrandPurchaseEvent = useCallbackEvent((params: {
    projectCode: ProjectCode
    transactionId: string
  }) => {
    if (!userId) return
    sendEvent({
      eventName: 'stm_purchase',
      eventId: params.transactionId,
      eventParams: {
        event_category: 'purchase',
        project_code: params.projectCode,
        user_id: userId,
      },
    })
  })

  const sendCrossBrandAddToCartEvent = useCallbackEvent((params: {
    projectCode: ProjectCode
    sectionName: StmSectionNameType
  }) => {
    if (!userId) return
    sendEvent({
      eventName: 'stm_add_to_cart',
      eventParams: {
        event_category: 'add_to_cart',
        section_name: params.sectionName,
        project_code: params.projectCode,
        user_id: userId,
      },
    })
  })

  return {
    sendCrossBrandBannerClickEvent,
    sendBrandNavClickEvent,
    sendCrossBrandPageViewEvent,
    sendCrossBrandPurchaseEvent,
    sendCrossBrandAddToCartEvent,
  }
}
